/* eslint-disable react-refresh/only-export-components -- módulo de rotas: páginas lazy + wrapper L */
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppShell } from "./layouts/AppShell";
import { RequireAuth } from "./layouts/RequireAuth";
import { AgentIndexGate } from "@/features/softphone/AgentIndexGate";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/ForgotPasswordPage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const UsersPage = lazy(() => import("@/features/users/UsersPage"));
const TagsPage = lazy(() => import("@/features/tags/TagsPage"));
const QueuesPage = lazy(() => import("@/features/queues/QueuesPage"));
const ExtensionsPage = lazy(() => import("@/features/extensions/ExtensionsPage"));
const BreaksPage = lazy(() => import("@/features/breaks/BreaksPage"));
const BreakApprovalPage = lazy(() => import("@/features/breaks/BreakApprovalPage"));
const ActivityRecordsPage = lazy(() => import("@/features/records/ActivityRecordsPage"));
const AgentRecordsPage = lazy(() => import("@/features/records/AgentRecordsPage"));
const ClientRecordsPage = lazy(() => import("@/features/records/ClientRecordsPage"));
const ServiceScorePage = lazy(() => import("@/features/records/ServiceScorePage"));
const StaticSettingsPage = lazy(() => import("@/features/settings/StaticSettingsPage"));
const ServiceNowPage = lazy(() => import("@/features/settings/ServiceNowPage"));
const OutboundCampaignsPage = lazy(() => import("@/features/outbound/CampaignsPage"));
const OutboundCampaignDetailPage = lazy(() => import("@/features/outbound/CampaignDetailPage"));
const OutboundDncPage = lazy(() => import("@/features/outbound/DncPage"));
const OutboundIntegrationPage = lazy(() => import("@/features/outbound/NimoIntegrationPage"));
const JourneyDemoPage = lazy(() => import("@/features/journey/JourneyDemoPage"));
const AgentWorkspacePage = lazy(() => import("@/features/softphone/AgentWorkspacePage"));
const DesignSystemPage = lazy(() => import("@/features/design/DesignSystemPage"));
const AnalyticsRoutes = lazy(() => import("@/features/analytics/AnalyticsRoutes"));

/** Discador: leitura para REPORT; gestão (ADMIN/SUPER) é controlada dentro das telas. */
const OUTBOUND_ROLES = ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"];

function L({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div role="status" aria-live="polite" className="p-6 text-body text-fg-subtle">
          Carregando…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <L>
        <LoginPage />
      </L>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <L>
        <ForgotPasswordPage />
      </L>
    ),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <RequireAuth roles={["ROLE_USER", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <AgentIndexGate>
              <L>
                <DashboardPage />
              </L>
            </AgentIndexGate>
          </RequireAuth>
        ),
      },
      {
        path: "atendimento",
        element: (
          <RequireAuth roles={["ROLE_USER", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <AgentWorkspacePage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "users",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <UsersPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "tags",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <TagsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "queues",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <QueuesPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "extensions",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <ExtensionsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "breaks",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <BreaksPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "break-approval",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <BreakApprovalPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "records/activity",
        element: (
          <RequireAuth roles={["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <ActivityRecordsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "records/agent",
        element: (
          <RequireAuth roles={["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <AgentRecordsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "records/client",
        element: (
          <RequireAuth roles={["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <ClientRecordsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "records/service-score",
        element: (
          <RequireAuth roles={["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <ServiceScorePage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "settings/static",
        element: (
          <RequireAuth roles={["ROLE_SUPER"]}>
            <L>
              <StaticSettingsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "settings/servicenow",
        element: (
          <RequireAuth roles={["ROLE_SUPER"]}>
            <L>
              <ServiceNowPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "outbound",
        element: (
          <Navigate to="/outbound/campaigns" replace />
        ),
      },
      {
        path: "outbound/campaigns",
        element: (
          <RequireAuth roles={OUTBOUND_ROLES}>
            <L>
              <OutboundCampaignsPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "outbound/campaigns/:id",
        element: (
          <RequireAuth roles={OUTBOUND_ROLES}>
            <L>
              <OutboundCampaignDetailPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "outbound/dnc",
        element: (
          <RequireAuth roles={OUTBOUND_ROLES}>
            <L>
              <OutboundDncPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "outbound/integration",
        element: (
          <RequireAuth roles={OUTBOUND_ROLES}>
            <L>
              <OutboundIntegrationPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        // Relatórios gerenciais (sub-rotas em features/analytics/AnalyticsRoutes).
        path: "analytics/*",
        element: (
          <RequireAuth roles={["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <AnalyticsRoutes />
            </L>
          </RequireAuth>
        ),
      },
      {
        // Vitrine viva do design system (fora do menu).
        path: "design",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <DesignSystemPage />
            </L>
          </RequireAuth>
        ),
      },
      {
        path: "jornada",
        element: (
          <RequireAuth roles={["ROLE_ADMIN", "ROLE_SUPER"]}>
            <L>
              <JourneyDemoPage />
            </L>
          </RequireAuth>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
], {
  // Deploy sob subcaminho (ex.: /phonemanagergui/) — vem do `base` do Vite (VITE_BASE_PATH).
  basename: import.meta.env.BASE_URL.replace(/\/+$/, "") || "/",
});
